import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getStores, Store } from '../../services/storeService';
import { trackStoresDiscoveryView, trackStoreView } from '../../utils/clubStoreAnalytics';
import { MagnifyingGlassIcon, MapPinIcon } from '@heroicons/react/24/outline';

const StoresDiscoveryPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStores();
    // Track analytics when page is viewed
    if (user?.id) {
      trackStoresDiscoveryView(user.role || undefined).catch(console.error);
    }
  }, [user?.id]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      if (!loading) {
        loadStores();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadStores = async () => {
    try {
      setError(null);
      setLoading(true);
      const fetchedStores = await getStores({
        search: searchQuery || undefined,
        city: user?.preferredCity || undefined,
      });
      setStores(fetchedStores);
      // Clear error if we successfully got an empty array (no stores available)
      if (fetchedStores.length === 0) {
        setError(null);
      }
    } catch (err: any) {
      // This should rarely happen now since getStores handles errors internally
      console.error('Error fetching stores:', err);
      setError(null); // Don't show error, just show empty state
      setStores([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStoreClick = async (store: Store) => {
    if (user?.id) {
      await trackStoreView(store.id, 'StoresDiscovery', user.role || undefined).catch(console.error);
    }
  };

  if (loading && stores.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center">{t('common.loading') || 'Loading stores...'}</div>
      </div>
    );
  }

  if (error && stores.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadStores}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            {t('common.retry') || 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('stores.searchPlaceholder') || 'Search stores...'}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <span className="text-gray-400 hover:text-gray-600">×</span>
            </button>
          )}
        </div>
      </div>

      {/* Stores List */}
      {stores.length === 0 ? (
        <div className="text-center py-12">
          <MapPinIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {searchQuery 
              ? (t('stores.noStoresFound') || 'No stores found')
              : (t('stores.noStores') || 'No stores available')}
          </h3>
          {!searchQuery && (
            <p className="mt-1 text-sm text-gray-500">
              {t('stores.checkBackLater') || 'Check back later for new stores'}
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stores.map((store) => (
            <Link
              key={store.id}
              to={`/stores/${store.id}`}
              onClick={() => handleStoreClick(store)}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow p-6"
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{store.name}</h3>
                {store.isActive && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {t('common.active') || 'Active'}
                  </span>
                )}
              </div>
              {store.location && (
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <MapPinIcon className="h-4 w-4 mr-1" />
                  <span>{store.location}</span>
                </div>
              )}
              {store.city && (
                <div className="text-sm text-primary-600 font-medium">
                  {store.city}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default StoresDiscoveryPage;
